import { useMemo, useSyncExternalStore } from 'react'
import {
  readLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
  subscribeToLocalStorageItem,
  useLocalStorage
} from 'shared-ui/util-hooks'
import { Enum } from 'utils'

const createKey = (flag: FeatureFlag) => `--panorama-feature-flag-${flag}`

export const useFeatureFlag = (flag: FeatureFlag) => {
  const [isSavedAsEnabled] = useLocalStorage({
    defaultValue: false,
    key: createKey(flag)
  })

  return isSavedAsEnabled
}

export const useEnableFeatureFlag = () => {
  return {
    enable: (flag: FeatureFlag) => {
      setLocalStorageItem(createKey(flag), true)
    },
    reset: () => {
      Object.values(FeatureFlag).forEach((flag) => {
        removeLocalStorageItem(createKey(flag))
      })
    }
  }
}

export enum FeatureFlag {
  Guidance = 'guidance',
  Debug = 'debug',
  LiveStreamDesktop = 'appview',
  TrialCode = 'X9T3P7LQ2M',
  BetaFileUpload = 'betaFileUpload',
  RealtimeConversation = 'realtimeConversation',
  GptLive = 'gptLive',
  ShapefileApplicationDataExport = 'shapefile_export',
  LivestreamAddon = 'livestreamAddon'
}

export type FeatureFlagName = keyof typeof FeatureFlag

const subscribeToFeatureFlags = (callback: () => void) => {
  const unsubscribes = Enum.values(FeatureFlag).map((flag) =>
    subscribeToLocalStorageItem(createKey(flag), callback)
  )
  return () => unsubscribes.forEach((unsubscribe) => unsubscribe())
}

// Joined into a string because useSyncExternalStore compares snapshots by identity, and a fresh
// array would never compare equal.
const readEnabledFeatureFlags = (): string =>
  Enum.keys(FeatureFlag)
    .filter((name) => readLocalStorageItem(createKey(FeatureFlag[name]), false))
    .join(',')

/**
 * Every flag enabled in this browser, by enum member name (`LiveStreamDesktop`, not `appview`).
 * Code refers to a flag by its member name, so that is the form that matches what a reader of
 * the source reports, and it keeps obfuscated values such as the trial code out of anything we
 * send.
 */
export const useEnabledFeatureFlags = (): FeatureFlagName[] => {
  const joined = useSyncExternalStore(subscribeToFeatureFlags, readEnabledFeatureFlags)
  return useMemo(() => {
    const enabled = new Set(joined.split(','))
    return Enum.keys(FeatureFlag).filter((name) => enabled.has(name))
  }, [joined])
}
